import { Popover, Avatar, Typography, Button } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { theme } from 'antd';

const { Text } = Typography;

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
}

// Function to remove Polish characters
function removePolishChars(str: string): string {
    const map: Record<string, string> = {
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n',
        'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
        'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N',
        'Ó': 'O', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z',
    };
    return str.split('').map(char => map[char] || char).join('');
}

function generateLogin(firstName: string, lastName: string): string {
    const first = removePolishChars(firstName.toLowerCase().trim());
    const last = removePolishChars(lastName.toLowerCase().trim());
    return `${first}.${last}`;
}

interface Props {
    content: string;
}

/**
 * Renders text with @mentions highlighted as clickable links with popup cards (Trello-style)
 * Parses format: @login[uuid] -> highlighted @login with popup
 */
export default function MentionDisplay({ content }: Props) {
    const { token } = theme.useToken();
    const navigate = useNavigate();

    // Fetch all users for lookup
    const { data: usersData } = useQuery({
        queryKey: ['users-list'],
        queryFn: () => usersApi.getAll().then((r: any) => r.data),
    });
    const users: User[] = usersData || [];

    // Parse mentions: @login[uuid] format with brackets
    const mentionRegex = /@([a-z0-9.]+)\[([^\]]+)\]/g;
    const parts: Array<{ type: 'text' | 'mention'; value: string; userId?: string; login?: string }> = [];

    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
        if (match.index > lastIndex) {
            parts.push({ type: 'text', value: content.slice(lastIndex, match.index) });
        }

        parts.push({
            type: 'mention',
            value: match[0],
            login: match[1],
            userId: match[2],
        });

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
        parts.push({ type: 'text', value: content.slice(lastIndex) });
    }

    // If no mentions found, just return the content as-is
    if (parts.length === 0) {
        return <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>;
    }

    return (
        <span style={{ whiteSpace: 'pre-wrap' }}>
            {parts.map((part, index) => {
                if (part.type === 'text') {
                    return <span key={index}>{part.value}</span>;
                }

                const user = users.find(u => u.id === part.userId);
                const displayLogin = part.login || (user ? generateLogin(user.firstName, user.lastName) : 'user');

                const popoverContent = (
                    <div style={{
                        background: token.colorBgElevated,
                        padding: 16,
                        minWidth: 220,
                        borderRadius: 8,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <Avatar
                                size={48}
                                style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}
                                icon={<UserOutlined />}
                            >
                                {user?.firstName?.[0] || displayLogin[0]?.toUpperCase()}
                            </Avatar>
                            <div>
                                <Text strong style={{ fontSize: 15, color: token.colorText, display: 'block' }}>
                                    {user ? `${user.firstName} ${user.lastName}` : displayLogin}
                                </Text>
                                <Text style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                    @{displayLogin}
                                </Text>
                            </div>
                        </div>
                        <Button
                            type="text"
                            block
                            onClick={() => navigate(`/profile/${part.userId}`)}
                            style={{
                                color: token.colorPrimary,
                                textAlign: 'left',
                                padding: '8px 0',
                            }}
                        >
                            Zobacz profil
                        </Button>
                    </div>
                );

                return (
                    <Popover
                        key={index}
                        content={popoverContent}
                        trigger="click"
                        placement="top"
                        overlayInnerStyle={{
                            background: token.colorBgElevated,
                            padding: 0,
                            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                        }}
                    >
                        <span
                            style={{
                                color: '#579dff',
                                cursor: 'pointer',
                                fontWeight: 500,
                            }}
                        >
                            @{displayLogin}
                        </span>
                    </Popover>
                );
            })}
        </span>
    );
}
